<template>
  <div>
    <div>
      <div class="my-3">
        <h1 class="h1 text-grey-light-900 font-semibold text-3xl mb-3">Welcome back!</h1>
        <p class="text-sm font-normal text-black">
          We missed you. Let's get you signed in - please enter your details.
        </p>
        <ErrorDisplay v-if="errorMessage" :error-message="errorMessage" />
      </div>
    </div>

    <div v-if="isLoading && userStore.user" class="text-center">
      <LayupSpinner class="text-brand-700" small />
      <p>Signing in...</p>
    </div>
    <form id="loginForm" class="py-2.5" v-else>
      <UsernameInput
        v-model:username="username"
        v-model:is-username-valid="isUsernameValid"
        :label="'Username'"
        :disabled="isLoading"
        class="cellnumber text-left"
        @error="
          (error) => {
            errorMessage = error;
          }
        "
      />
      <PasswordInput v-model="password" :disabled="isLoading" :label="`Password`" class="password text-left" />

      <div class="flex flex-row my-3">
        <div class="flex-1 text-left text-sm">
          <input
            type="checkbox"
            v-model="rememberMe"
            name="rememberMe"
            class="rounded-full mr-2 focus:bg-brand-300 focus:ring-brand-500 focus:shadow-outline focus:ring-2"
          />
          <span class="text-semibold text-brand-700 text-sm">Remember me</span>
        </div>
        <RouterLink class="text-semibold text-brand-700 text-sm" to="forgot-password">Forgot password</RouterLink>
      </div>
      <TurnstileWidget @success="handleSuccess" />
      <LayupButton
        :label="'Sign in'"
        :isLoading="isLoading"
        @click="onSubmit"
        :disabled="isLoading || !isUsernameValid || !turnstileToken"
        data-testid="sign_in_button"
      />

      <div class="text-center mt-3">
        <p class="text-grey-light-500 text-sm">
          Don't have an account?
          <RouterLink class="btn text-brand-700 text-semibold text-sm" to="" @click="temporarySignUp">
            Sign up
          </RouterLink>
        </p>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import ErrorDisplay from '@/components/common/ErrorDisplay.vue';
import LayupButton from '@/components/common/LayupButton.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import PasswordInput from '@/components/common/PasswordInput.vue';
import TurnstileWidget from '@/components/common/TurnstileWidget.vue';
import UsernameInput from '@/components/common/UsernameInput.vue';
import { MERCHANT_UI_URL } from '@/config';
import { NetworkError } from '@/errors';
import { signIn } from '@/services/auth';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onUnmounted, ref } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

const props = defineProps<{
  channel: string;
}>();

const userStore = useUserStore();
const merchantStore = useMerchantStore();

const errorMessage = ref('');
const isLoading = ref(false);
const username = ref('');
const password = ref('');
const rememberMe = ref(false);
const isUsernameValid = ref(false);

const router = useRouter();
const route = useRoute();
const redirect = route.query.redirect as string;
const turnstileToken = ref('');

const handleSuccess = (token: string) => {
  turnstileToken.value = token;
};

const temporarySignUp = () => {
  if (props.channel === 'merchant') {
    window.location.href = `${MERCHANT_UI_URL}/register`;
  } else {
    router.push({ name: 'shopperSignUp' });
  }
};

const onSubmit = async () => {
  try {
    isLoading.value = true;
    errorMessage.value = '';
    const user = await signIn(username.value, password.value, rememberMe.value, turnstileToken.value);
    userStore.signIn(user, rememberMe.value);
    if (user.merchantIds?.[0]) await merchantStore.onChangeMerchant(user.merchantIds[0]);

    if (userStore.user.twoFactorAuthenticationRequired) {
      addMetric('sign-in', { status: 'two_factor_authentication_required', channel: props.channel });
      router.push({ name: 'merchantOtpVerification' });
    } else {
      if (!merchantStore.activeMerchantId && userStore.user.merchantIds?.length) {
        merchantStore.activeMerchantId = userStore.user.merchantIds[0];
      }

      addMetric('sign-in', { status: 'success', channel: props.channel });
      if (redirect) {
        router.push(redirect);
      } else {
        router.push({ name: 'merchantDashboardHome' });
      }
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('sign-in', { status: 'network_error', channel: props.channel });
      errorMessage.value = 'Network Error. Please try again.';
    } else if (error.response?.status === 400 || error.response?.status === 401) {
      addMetric('sign-in', { status: 'failed', channel: props.channel });
      errorMessage.value = 'Incorrect sign-in credentials';
    } else {
      createException(error);
      addMetric('sign-in', { status: 'error' });
      errorMessage.value = 'An unexpected error occurred. Please try again.';
    }
    isLoading.value = false;
  }
};

onUnmounted(() => {
  isLoading.value = false;
});
</script>
